import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { FaUser, FaLock, FaEye, FaEyeSlash, FaArrowRight } from 'react-icons/fa';
import Swal from 'sweetalert2';

const Login = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, loading, user } = useAuth();
  const [formLoading, setFormLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState(null);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    if (isAuthenticated) {
      const dest = user?.role === 'parent' ? '/parent/dashboard' : '/app/dashboard';
      navigate(dest, { replace: true });
    }
  }, [isAuthenticated, navigate, user?.role]);

  useEffect(() => {
    if (loginError) {
      Swal.fire({
        icon: 'error',
        title: 'Échec de connexion',
        html: `
          <div style="text-align: center; padding: 10px 0;">
            <p style="font-size: 16px; color: #333; margin-bottom: 10px; font-weight: 500;">
              ${loginError}
            </p>
            <p style="font-size: 14px; color: #666; margin-top: 10px;">
              Veuillez vérifier vos identifiants et réessayer.
            </p>
          </div>
        `,
        confirmButtonText: 'Réessayer',
        confirmButtonColor: '#2563eb',
        allowOutsideClick: false,
        allowEscapeKey: true,
        width: '450px',
        padding: '2rem',
        customClass: {
          container: 'swal2-container-custom',
          popup: 'swal2-popup-custom',
        },
        didOpen: () => {
          const container = document.querySelector('.swal2-container');
          if (container) {
            container.style.zIndex = '999999';
          }
        },
        didClose: () => setLoginError(null),
      });
    }
  }, [loginError]);

  if (loading) return null;
  if (isAuthenticated) {
    const dest = user?.role === 'parent' ? '/parent/dashboard' : '/app/dashboard';
    return <Navigate to={dest} replace />;
  }

  const onSubmit = async (data) => {
    setFormLoading(true);
    const result = await login(data);
    setFormLoading(false);

    if (result.success) {
      toast.success('Connexion réussie!');
      const dest = result.user?.role === 'parent' ? '/parent/dashboard' : '/app/dashboard';
      navigate(dest, { replace: true });
    } else {
      setLoginError('Nom d\'utilisateur ou mot de passe incorrect');
    }
  };

  return (
    <div className="min-h-screen flex bg-white overflow-hidden">
      {/* Panneau gauche — ratio 3:2 (image 1536×1024), occupe toute la hauteur */}
      <div
        className="hidden lg:block relative flex-shrink-0 h-screen overflow-hidden bg-slate-950"
        style={{ width: 'min(calc(100vh * 3 / 2), calc(100vw - 28rem))' }}
      >
        <img
          src={process.env.PUBLIC_URL + '/images/login_page_image.png'}
          alt="Schola — Gestion scolaire réinventée"
          className="w-full h-full object-contain"
        />
        <p className="absolute top-0 left-0 right-0 text-center text-white text-lg font-light px-6 pt-4 leading-relaxed drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]">
          La gestion scolaire réinventée pour{' '}
          <span className="text-cyan-400 font-medium">l'avenir.</span>
        </p>
      </div>

      {/* Panneau droit — formulaire */}
      <div className="flex flex-1 flex-col min-h-screen min-w-0 w-full">
        <div className="flex-1 flex items-center justify-center px-4 sm:px-6 md:px-10 py-8 sm:py-10">
          <div className="w-full max-w-[400px]">
            {/* Logo Schola */}
            <div className="mb-10 flex justify-center">
              <img
                src={process.env.PUBLIC_URL + '/logo_schola.jpg'}
                alt="Logo Schola"
                className="h-16 w-auto object-contain"
                style={{ maxWidth: 200 }}
              />
            </div>

            {/* En-tête */}
            <div className="mb-6 sm:mb-8">
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
                Connexion
              </h1>
              <p className="mt-2 text-slate-500 text-[15px]">
                Accédez à votre espace de gestion scolaire
              </p>
            </div>

            {/* Formulaire */}
            <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-slate-700 mb-1.5">
                  Identifiant ou email
                </label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">
                    <FaUser size={15} />
                  </span>
                  <input
                    {...register('username', { required: 'Identifiant ou email requis' })}
                    id="username"
                    type="text"
                    autoComplete="username"
                    className="pl-10 pr-4 py-3 w-full rounded-xl bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400 text-sm transition focus:bg-white focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none"
                    placeholder="Nom d'utilisateur ou adresse email"
                  />
                </div>
                {errors.username && (
                  <p className="mt-1.5 text-xs text-red-500">{errors.username.message}</p>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label htmlFor="password" className="block text-sm font-medium text-slate-700">
                    Mot de passe
                  </label>
                  <a href="/forgot-password" className="text-xs text-primary-600 hover:underline">
                    Mot de passe oublié ?
                  </a>
                </div>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">
                    <FaLock size={15} />
                  </span>
                  <input
                    {...register('password', { required: 'Mot de passe requis' })}
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    className="pl-10 pr-11 py-3 w-full rounded-xl bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400 text-sm transition focus:bg-white focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none"
                    placeholder="Entrez votre mot de passe"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition"
                    tabIndex={-1}
                    aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                  >
                    {showPassword ? <FaEyeSlash size={15} /> : <FaEye size={15} />}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1.5 text-xs text-red-500">{errors.password.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={formLoading}
                className="group relative w-full flex items-center justify-center gap-2 py-3 px-4 mt-2 text-sm font-semibold rounded-xl text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-4 focus:ring-primary-500/30 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-primary-600/25 transition-all duration-200"
              >
                {formLoading ? (
                  <>
                    <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Connexion en cours...
                  </>
                ) : (
                  <>
                    Se connecter
                    <FaArrowRight size={13} className="transition-transform group-hover:translate-x-0.5" />
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 pt-6 border-t border-slate-200 text-center">
              <p className="text-sm text-slate-500 mb-2">Vous êtes parent d'élève ?</p>
              <a
                href="/parent/request-account"
                className="text-sm font-medium text-primary-600 hover:text-primary-700 hover:underline"
              >
                Demander un compte espace parents
              </a>
            </div>
          </div>
        </div>

        {/* Footer TPSoft */}
        <div className="px-6 sm:px-10 pb-8 flex flex-col items-center gap-2">
          <img
            src={process.env.PUBLIC_URL + '/tpsoft_copyright.png'}
            alt="TPSoft"
            className="h-10 w-auto object-contain opacity-70"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <span className="text-xs text-slate-400 text-center">
            TPSoft Informatique - Juin 2025 - Tous droits réservés.
          </span>
        </div>
      </div>
    </div>
  );
};

export default Login;
